/* eslint-disable perfectionist/sort-modules, perfectionist/sort-union-types */
import { isAxiosError } from 'axios';

const API_DETAIL_MESSAGES: Record<string, string> = {
  'escrow account opening already finished': 'A abertura de conta escrow já foi finalizada.',
};

const API_ERROR_MESSAGES: Record<string, string> = {
  "can't be blank": 'não pode ficar em branco',
  'has already been taken': 'já está em uso',
  'is invalid': 'é inválido',
};

const EMAIL_TAKEN_MESSAGE = 'Este e-mail já está cadastrado';

const ASSOCIATE_BUSINESS_FIELD_ERRORS: Record<string, Partial<Record<'userId', string>>> = {
  user_already_bound_to_company_associate: {
    userId: 'Este usuário já está vinculado a outro sócio. Escolha outro usuário.',
  },
  user_partner_mismatch: {
    userId: 'O usuário selecionado não pertence ao parceiro desta empresa.',
  },
};

const ASSOCIATE_BUSINESS_ERROR_MESSAGES: Record<string, string> = {
  associate_document_not_in_company: 'CPF não é sócio desta empresa.',
  cannot_delete_main_associate: 'Não é possível remover o sócio administrador principal.',
  user_already_bound_to_company_associate: 'Este usuário já está vinculado a outro sócio. Escolha outro usuário.',
  user_partner_mismatch: 'O usuário selecionado não pertence ao parceiro desta empresa.',
};

export type AssociateFormField = 'celular' | 'nomeCompleto' | 'userId';

export type CoreUserFormField = 'celular' | 'email' | 'nomeCompleto';

export type FormApiErrorResult<TField extends string> = {
  fieldErrors: Partial<Record<TField, string>>;
  formError: string | null;
};

type FieldPathResolver = (_path: string[]) => string | null;

function getErrorResponseData(error: unknown): Record<string, unknown> | undefined {
  if (isAxiosError(error)) {
    return error.response?.data as Record<string, unknown> | undefined;
  }
  return (error as { response?: { data?: Record<string, unknown> } })?.response?.data;
}

const GENERAL_ERROR_TOAST_STATUSES = new Set([404]);

export function getAxiosErrorStatus(error: unknown): number | undefined {
  return isAxiosError(error) ? error.response?.status : undefined;
}

export function shouldShowGeneralApiErrorInToast(error: unknown): boolean {
  const status = getAxiosErrorStatus(error);
  return status !== undefined && GENERAL_ERROR_TOAST_STATUSES.has(status);
}

function translateErrorMessage(message: unknown): string {
  const text = String(message).trim();
  return API_ERROR_MESSAGES[text] ?? text;
}

function formatFieldMessages(key: string, messages: unknown[]): string {
  if (key === 'email' && messages.some((message) => String(message).trim() === 'has already been taken')) {
    return EMAIL_TAKEN_MESSAGE;
  }

  return messages.map((message) => translateErrorMessage(message)).join(', ');
}

function resolveCoreUserFormField(path: string[]): string | null {
  const key = path[path.length - 1];
  if (key === 'email' || key === 'celular' || key === 'nomeCompleto') return key;
  if (key === 'nome_completo') return 'nomeCompleto';
  if (key === 'mobile') return 'celular';
  return null;
}

function resolveAssociateFormField(path: string[]): string | null {
  const key = path[path.length - 1];
  if (key === 'full_name' || key === 'name') return 'nomeCompleto';
  if (key === 'phone' || key === 'mobile') return 'celular';
  if (key === 'user_id') return 'userId';
  return null;
}

function parseValidationDetailArray(
  detail: { loc?: (number | string)[]; msg?: string }[],
  resolveField: FieldPathResolver,
): Record<string, string> {
  return detail.reduce<Record<string, string>>((accumulator, item) => {
    const path = (item.loc ?? [])
      .filter((segment): segment is string => typeof segment === 'string' && segment !== 'body');
    const formField = resolveField(path);
    if (formField && item.msg) {
      const key = path[path.length - 1] ?? '';
      accumulator[formField] = formatFieldMessages(key, [item.msg]);
    }
    return accumulator;
  }, {});
}

function collectFieldErrors(
  obj: Record<string, unknown>,
  path: string[],
  resolveField: FieldPathResolver,
): Record<string, string> {
  return Object.entries(obj).reduce<Record<string, string>>((accumulator, [key, value]) => {
    const currentPath = [...path, key];

    if (Array.isArray(value)) {
      if (typeof value[0] === 'string' || value.length === 0) {
        const formField = resolveField(currentPath);
        if (formField) {
          accumulator[formField] = formatFieldMessages(key, value);
        }
        return accumulator;
      }

      return {
        ...accumulator,
        ...parseValidationDetailArray(
          value as { loc?: (number | string)[]; msg?: string }[],
          resolveField,
        ),
      };
    }

    if (value && typeof value === 'object') {
      return {
        ...accumulator,
        ...collectFieldErrors(value as Record<string, unknown>, currentPath, resolveField),
      };
    }

    return accumulator;
  }, {});
}

function getDetailsObject(data: Record<string, unknown>): Record<string, unknown> | null {
  const errorDetails = (data.error as { details?: unknown } | undefined)?.details;
  if (errorDetails && typeof errorDetails === 'object' && !Array.isArray(errorDetails)) {
    return errorDetails as Record<string, unknown>;
  }

  if (data.details && typeof data.details === 'object' && !Array.isArray(data.details)) {
    return data.details as Record<string, unknown>;
  }

  if (data.errors && typeof data.errors === 'object' && !Array.isArray(data.errors)) {
    return data.errors as Record<string, unknown>;
  }

  return null;
}

export function parseApiFieldErrors(
  error: unknown,
  resolveField: FieldPathResolver,
): Record<string, string> {
  const data = getErrorResponseData(error);
  if (!data) return {};

  const details = getDetailsObject(data);
  const fieldErrors = details
    ? collectFieldErrors(details, [], resolveField)
    : {};

  if (Array.isArray(data.detail)) {
    return {
      ...fieldErrors,
      ...parseValidationDetailArray(
        data.detail as { loc?: (number | string)[]; msg?: string }[],
        resolveField,
      ),
    };
  }

  return fieldErrors;
}

export function parseApiGeneralError(
  error: unknown,
  fallback: string,
  resolveField: FieldPathResolver,
): string {
  const fieldErrors = parseApiFieldErrors(error, resolveField);
  if (Object.keys(fieldErrors).length > 0) return '';

  const data = getErrorResponseData(error);
  if (!data) return fallback;

  if (typeof data.detail === 'string') {
    return API_DETAIL_MESSAGES[data.detail] ?? fallback;
  }

  if (typeof data.message === 'string') return data.message;

  return fallback;
}

export function parseCoreUserFieldErrors(
  error: unknown,
): Partial<Record<CoreUserFormField, string>> {
  return parseApiFieldErrors(error, resolveCoreUserFormField);
}

export function parseAssociateFieldErrors(
  error: unknown,
): Partial<Record<AssociateFormField, string>> {
  return parseApiFieldErrors(error, resolveAssociateFormField);
}

export function parseCoreUserGeneralError(error: unknown, fallback: string): string {
  return parseApiGeneralError(error, fallback, resolveCoreUserFormField);
}

export function parseAssociateBusinessFieldErrors(
  error: unknown,
): Partial<Record<'userId', string>> {
  const data = getErrorResponseData(error);
  const errors = (data?.detail as { errors?: { identifier?: string }[] } | undefined)?.errors;

  if (!Array.isArray(errors) || !errors[0]?.identifier) return {};

  return ASSOCIATE_BUSINESS_FIELD_ERRORS[errors[0].identifier] ?? {};
}

export function parseAssociateGeneralError(error: unknown, fallback: string): string {
  const fieldErrors = {
    ...parseAssociateFieldErrors(error),
    ...parseAssociateBusinessFieldErrors(error),
  };
  if (Object.keys(fieldErrors).length > 0) return '';

  const general = parseApiGeneralError(error, fallback, resolveAssociateFormField);
  if (general !== fallback) return general;

  const data = getErrorResponseData(error);
  const errors = (data?.detail as { errors?: { identifier?: string }[] } | undefined)?.errors;
  if (Array.isArray(errors) && errors[0]?.identifier) {
    const message = ASSOCIATE_BUSINESS_ERROR_MESSAGES[errors[0].identifier];
    if (message) return message;
  }

  if (Array.isArray(data?.detail)) {
    const messages = (data.detail as { msg?: string }[])
      .map((item) => item.msg)
      .filter(Boolean)
      .join('\n');
    return messages || fallback;
  }

  return fallback;
}

export function resolveCoreUserFormApiErrors(
  error: unknown,
  fallback: string,
): FormApiErrorResult<CoreUserFormField> {
  const fieldErrors = parseCoreUserFieldErrors(error);

  if (Object.keys(fieldErrors).length > 0) {
    return { fieldErrors, formError: null };
  }

  const formError = parseCoreUserGeneralError(error, fallback);
  return { fieldErrors, formError: formError || null };
}

export function resolveAssociateFormApiErrors(
  error: unknown,
  fallback: string,
): FormApiErrorResult<AssociateFormField> {
  const fieldErrors = {
    ...parseAssociateFieldErrors(error),
    ...parseAssociateBusinessFieldErrors(error),
  };

  if (Object.keys(fieldErrors).length > 0) {
    return { fieldErrors, formError: null };
  }

  const formError = parseAssociateGeneralError(error, fallback);
  return { fieldErrors, formError: formError || null };
}
/* eslint-enable perfectionist/sort-modules, perfectionist/sort-union-types */
